import PickupType from "../../enums/PickupType";
import Game from "../Game";
import GameCore from "../GameCore";
import BasePickup from "./BasePickup";
import PickupGroup from "./PickupGroup";
import hapticFeedback from "../../../utils/haptics";
import {
  SPRITESHEET_KEYS,
  SPRITESHEET_FRAMES,
} from "../../consts/SPRITESHEETS";

export default class GemPickup extends BasePickup {
  landingLocation: Phaser.Math.Vector2;
  curve: Phaser.Curves.CubicBezier;

  constructor(group: PickupGroup, x: number, y: number, value = 0) {
    super(group, x, y, PickupType.GEM, SPRITESHEET_KEYS.PICKUPS);
    this.landingLocation = new Phaser.Math.Vector2(0, 0);
    this.value = value;
    this.SetValue(value);
    this.setScale(1.5 * GameCore.PixelScale);

    const angle = 2 * (Math.random() - 0.5) * Math.PI;
    const amplitude = 100;
    const cosAngle = Math.cos(angle);
    const sinAngle = Math.sin(angle);
    const midPoint = {
      x: x + cosAngle * amplitude,
      y: y + sinAngle * amplitude * 2,
    };
    this.landingLocation.set(
      x + cosAngle * amplitude,
      y + sinAngle * amplitude
    );
    Game.Core.scene.tweens.add({
      targets: this,
      time: 1,
      duration: 500,
      ease: "Linear",
    });
    const startPoint = new Phaser.Math.Vector2(x, y);
    const controlPoint1 = new Phaser.Math.Vector2(midPoint.x, midPoint.y);
    const controlPoint2 = new Phaser.Math.Vector2(midPoint.x, midPoint.y);
    const endPoint = new Phaser.Math.Vector2(
      this.landingLocation.x,
      this.landingLocation.y
    );
    this.curve = new Phaser.Curves.CubicBezier(
      startPoint,
      controlPoint1,
      controlPoint2,
      endPoint
    );
  }

  SetValue(value: number) {
    this.value = value;

    if (value < 15) {
      this.setTexture(
        SPRITESHEET_KEYS.PICKUPS,
        SPRITESHEET_FRAMES.PICKUPS.GEM_BLUE
      );
    } else if (value < 30) {
      this.setTexture(
        SPRITESHEET_KEYS.PICKUPS,
        SPRITESHEET_FRAMES.PICKUPS.GEM_PURPLE
      );
    } else {
      this.setTexture(
        SPRITESHEET_KEYS.PICKUPS,
        SPRITESHEET_FRAMES.PICKUPS.GEM_GOLD
      );
    }
  }
  FollowCurve() {
    const out = new Phaser.Math.Vector2(0, 0);
    this.curve.getPoint(this.time, out);
    this.setPosition(out.x, out.y);
  }
  OnRecycle() {
    super.OnRecycle();
    // Reset texture based on current value
    this.SetValue(this.value);
    this.setTexture(
      SPRITESHEET_KEYS.PICKUPS,
      SPRITESHEET_FRAMES.PICKUPS.GEM_BLUE
    );
  }

  Update() {
    super.Update();
    this.setDepth(this.y - Game.Core.Player.y - this.scene.renderer.height);
  }

  GetTaken(): void {
    Game.Core.Player.xp += this.value * Game.Core.Player.growth;

    if (hapticFeedback.impactOccurred.isAvailable()) {
      hapticFeedback.impactOccurred("soft");
    }

    Game.Core.PlayerUI.Update();
    Game.Core.CheckForLevelUp();
    super.GetTaken();
  }

  /**
   * Override max size enforcement to consolidate gem value instead of culling
   */
  handleMaxSizeEnforcement(allGems: BasePickup[]): void {
    const CONSOLIDATION_DISTANCE = 100;
    const MAX_CONSOLIDATIONS = 3;

    // Use spatial grid to find nearby gems efficiently
    const nearbyGems = this.pool
      .findNearbyPickups(this.x, this.y, CONSOLIDATION_DISTANCE, PickupType.GEM)
      .filter((gem) => gem !== this && gem.active) as GemPickup[];

    if (nearbyGems.length > 0) {
      // Sort by distance and take closest gems
      const sortedNearbyGems = nearbyGems
        .map((gem) => ({
          gem,
          distanceSquared: Phaser.Math.Distance.Squared(
            this.x,
            this.y,
            gem.x,
            gem.y
          ),
        }))
        .sort((a, b) => a.distanceSquared - b.distanceSquared)
        .slice(0, MAX_CONSOLIDATIONS);

      // Distribute value among nearby gems
      const valuePerGem = this.value / sortedNearbyGems.length;

      for (const { gem } of sortedNearbyGems) {
        const newValue = gem.value + valuePerGem;
        gem.SetValue(Math.floor(newValue));
      }

      // Now safely remove the consolidated gem
      this.DeSpawn();
    } else {
      // No nearby gems to consolidate with, fall back to default culling
      super.handleMaxSizeEnforcement(allGems);
    }
  }
}
